import {LabelActions, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {
  TitleExtensionLanguage,
  TitleExtensionSuggestionResponse,
  useLazyGetTitleExtensionSuggestionQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

interface UseTitleExtensionGenerateActionConfig {
  vehicleId: string;
  /** Omitted for the base input, which resolves to the tenant's default content language. */
  language?: TitleExtensionLanguage;
  getCurrentValue: () => string;
  isDisabled: boolean;
  onGenerated: (text: string) => void;
}

/**
 * Builds the "Generate from equipment" label action for a title extension input.
 *
 * The suggestion is written into the form as a dirty value only — saving stays the form's
 * job, through the form's own Confirm. The text is never shortened: an over-limit
 * suggestion goes in whole and the field's counter and platform chips carry the warning.
 */
export function useTitleExtensionGenerateAction(
  config: UseTitleExtensionGenerateActionConfig
): LabelActions {
  const [getSuggestion, {isFetching}] = useLazyGetTitleExtensionSuggestionQuery();

  const applySuggestion = (response: TitleExtensionSuggestionResponse) =>
    match(response.emptyReason)
      .with(null, () => {
        if (response.text !== config.getCurrentValue()) {
          config.onGenerated(response.text);
        }
      })
      .with('no_list', () =>
        showNotification.info(i18n.t('page.advertisement.notifications.titleExtensionNoList'))
      )
      .with('nothing_to_add', () =>
        showNotification.info(i18n.t('page.advertisement.notifications.titleExtensionNothingToAdd'))
      )
      .otherwise(() =>
        showNotification.info(
          i18n.t('page.advertisement.notifications.titleExtensionNoMatchingEquipment')
        )
      );

  const handleGenerate = () =>
    getSuggestion({
      vehicleId: config.vehicleId,
      language: config.language,
      currentValue: config.getCurrentValue(),
    })
      .unwrap()
      .then(applySuggestion)
      .catch(handleApiError);

  return [
    {
      // The title stays constant on purpose: platform `Label` keys each action by its
      // title, so swapping it mid-flight would remount the Link and drop keyboard focus
      // the moment it is activated.
      title: i18n.t('page.advertisement.actions.generateTitleExtension'),
      // Matches the description Generate action in the same card.
      leftIcon: 'action/ai',
      // A Link has no loading state, so in-flight is signalled by disabling alone — which
      // is also what prevents a second click while the request is running.
      isDisabled: config.isDisabled || isFetching,
      onClick: handleGenerate,
    },
  ];
}
